import * as React from 'react';
import { t } from '@reos/i18n';
import { Check } from 'lucide-react';
import { PreparingSection } from '../PreparingSection.js';
import type { PreparingSectionCopy } from '../../lib/texts.js';
import type { ChecklistState } from '../../lib/field-intake-checklist.js';
import {
  addManualItem,
  attachCase,
  confirmItems,
  photoUrl,
  type IntakeConfirmResultDto,
  type IntakeItemDto,
  type IntakeSessionViewDto,
} from '../../lib/field-intake-api.js';
import { DomainApiError } from '../../lib/api-error.js';
import { ChecklistPanel, checklistKeys, marksFromItems, mergeMarks } from './ChecklistPanel.js';
import { INTAKE_FIELD_DEFS } from './fields.js';
import { fieldDef, fieldLabel, formatClock, formatValue, parseManualValue } from './values.js';

/**
 * Krok UXV4c body 5+6 — ROZBOR (predloha `Rozbor.png`, tablet `NaborTablet.png`).
 *
 * Kazdy navrh: pole z pevneho seznamu, hodnota, citace a cas v nahravce,
 * puvod „tvrdí majitel"; rozpor s katastrem zvyrazneny. Makler potvrdi po
 * jednom (zaskrtnuti) nebo vse jednim tlacitkem. Bez AI jen prepis + rucni
 * zarazeni; bez prepisu sedy stav „Připravujeme" (nahravka je ulozena).
 *
 * Krok NAB1:
 *  - navrh z nahravky, ktery se tluce s hodnotou ZAPSANOU RUCNE, NENI predem
 *    zaskrtnuty a nese upozorneni — potvrzeni prepisu je vedomy klik
 *    (zadani: „rozbor nahravky nesmi prepsat rucni hodnotu bez potvrzeni");
 *  - „Ještě nevíme" je tentyz RUCNI CHECKLIST jako pri nahravani, ne mrtva
 *    kolecka (nalez R.B. 7. 10. 2026 bod 2).
 */

export interface CaseOption {
  readonly id: string;
  readonly title: string;
}

const tr = (key: Parameters<typeof t>[0], values?: Record<string, string | number>): string =>
  t(key, { locale: 'cs', ...(values !== undefined ? { values } : {}) });

const TRANSCRIPT_PREPARING: PreparingSectionCopy = {
  title: tr('webAdmin.fieldIntake.review.transcriptPreparingTitle'),
  perex: tr('webAdmin.fieldIntake.review.transcriptPreparingPerex'),
  bullets: [tr('webAdmin.fieldIntake.review.transcriptPreparingBullet1'), tr('webAdmin.fieldIntake.review.transcriptPreparingBullet2')],
};

function itemSub(item: IntakeItemDto, hint: { factKey: string; value: unknown } | undefined): { text: string; warn: boolean } {
  const time = formatClock(item.atSeconds);
  if (item.status === 'conflict' && item.conflict !== null && item.factKey !== null) {
    return { text: tr('webAdmin.fieldIntake.review.conflictSaved', { value: formatValue(item.factKey, item.conflict.officialValue) }), warn: true };
  }
  if (hint !== undefined) {
    return {
      text: tr('webAdmin.fieldIntake.review.ownerClaimsRegistry', { field: fieldLabel(hint.factKey).toLowerCase(), value: formatValue(hint.factKey, hint.value) }),
      warn: true,
    };
  }
  if (item.quote !== null) return { text: `„${item.quote}“ · ${time}`, warn: false };
  return { text: item.atSeconds === null ? tr('webAdmin.fieldIntake.review.ownerClaims') : `${tr('webAdmin.fieldIntake.review.ownerClaims')} · ${time}`, warn: false };
}

/**
 * Krok NAB1 — hodnota pole, kterou makler zapsal RUCNE (navrh s puvodem
 * `manual`). Podle ni se rozhoduje, jestli smi byt navrh z nahravky predem
 * zaskrtnuty: dve polozky tehoz pole se pri potvrzeni zapisuji v poradi
 * seznamu, takze zaskrtnute oboji = rucni hodnotu prepsal rozbor.
 */
function manualValues(items: readonly IntakeItemDto[]): ReadonlyMap<string, unknown> {
  const out = new Map<string, unknown>();
  for (const item of items) {
    if (item.kind === 'fact' && item.origin === 'manual' && item.factKey !== null && item.status !== 'rejected') {
      out.set(item.factKey, item.value);
    }
  }
  return out;
}

/** Navrh z nahravky, ktery by prepsal jinou hodnotu zapsanou rucne. */
function overwritesManual(item: IntakeItemDto, manual: ReadonlyMap<string, unknown>): boolean {
  if (item.kind !== 'fact' || item.origin === 'manual' || item.factKey === null) return false;
  if (!manual.has(item.factKey)) return false;
  return JSON.stringify(manual.get(item.factKey) ?? null) !== JSON.stringify(item.value ?? null);
}

export function ReviewStep({
  view,
  cases,
  marks,
  onMark,
  onUnmark,
  onChanged,
  onOpenCase,
  onOpenListing,
}: {
  readonly view: IntakeSessionViewDto;
  readonly cases: readonly CaseOption[];
  /** Krok NAB1 — rucne odskrtnute body (stav drzi obrazovka `/nabor`). */
  readonly marks: ChecklistState;
  readonly onMark: (factKey: string, value: unknown) => void;
  readonly onUnmark: (factKey: string) => void;
  readonly onChanged: () => void;
  readonly onOpenCase: (caseId: string) => void;
  /** Krok MRGI1: predani na „Nový inzerát" zakazky (fakta uz tam jsou, jen se ctou). */
  readonly onOpenListing: (caseId: string) => void;
}): React.JSX.Element {
  const proposed = view.items.filter((i) => i.status === 'proposed');
  const manual = manualValues(view.items);
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(
    () => new Set(proposed.filter((i) => !overwritesManual(i, manual)).map((i) => i.id)),
  );
  const [result, setResult] = React.useState<IntakeConfirmResultDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [caseId, setCaseId] = React.useState('');
  const [manualKey, setManualKey] = React.useState(INTAKE_FIELD_DEFS[0]?.factKey ?? '');
  const [manualValue, setManualValue] = React.useState('');
  const [manualQuote, setManualQuote] = React.useState('');

  React.useEffect(() => {
    const fresh = view.items.filter((i) => i.status === 'proposed');
    const manualNow = manualValues(view.items);
    setSelected(new Set(fresh.filter((i) => !overwritesManual(i, manualNow)).map((i) => i.id)));
  }, [view]);

  const facts = view.items.filter((i) => i.kind === 'fact' && i.status !== 'rejected');
  const tasks = view.items.filter((i) => i.kind === 'task' && i.status !== 'rejected');
  const hints = new Map(view.hints.map((h) => [h.itemId, h]));
  const minutes = Math.max(1, Math.round(view.recordings.reduce((s, r) => s + r.durationS, 0) / 60));
  const transcript = view.session.transcript ?? [];

  const toggle = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (view.session.caseId === null) {
        if (caseId === '') throw new Error(tr('webAdmin.fieldIntake.review.pickCaseFirst'));
        await attachCase(view.session.id, caseId);
      }
      const ids = proposed.filter((i) => selected.has(i.id)).map((i) => i.id);
      setResult(await confirmItems(view.session.id, ids));
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('webAdmin.fieldIntake.review.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const addManual = async (): Promise<void> => {
    const value = parseManualValue(manualKey, manualValue);
    if (value === null) {
      setError(tr('webAdmin.fieldIntake.review.manualInvalid'));
      return;
    }
    const segment = transcript.find((s) => manualQuote !== '' && s.text === manualQuote);
    try {
      await addManualItem(view.session.id, {
        factKey: manualKey,
        value,
        quote: segment?.text ?? null,
        atSeconds: segment?.startS ?? null,
      });
      setManualValue('');
      setError(null);
      onChanged();
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : tr('webAdmin.fieldIntake.review.saveFailed'));
    }
  };

  const renderItem = (item: IntakeItemDto): React.ReactNode => {
    const sub = overwritesManual(item, manual)
      ? {
          text: tr('webAdmin.fieldIntake.review.manualWins', { value: formatValue(item.factKey ?? '', manual.get(item.factKey ?? '')) }),
          warn: true,
        }
      : itemSub(item, hints.get(item.id));
    const decided = item.status !== 'proposed';
    const label = item.kind === 'task' ? (item.title ?? '') : fieldLabel(item.factKey ?? '');
    return (
      <div key={item.id} className="rh-v4-row rh-v4-row--sub rh-v4-row--static rh-fi-item" data-testid={`fi-item-${item.factKey ?? 'task'}`}>
        <span className="rh-v4-row__text">
          <span className="rh-v4-row__title">
            {item.kind === 'fact' ? (
              <>
                {label}: <strong>{formatValue(item.factKey ?? '', item.value)}</strong>
              </>
            ) : (
              label
            )}
          </span>
          <span className={`rh-v4-row__sub rh-fi-said${sub.warn ? ' rh-fi-warn' : ''}`} data-testid="fi-item-sub">
            {sub.text}
          </span>
          {decided ? (
            <span className="rh-v4-row__sub rh-fi-item__status" data-testid="fi-item-status">
              {tr(`webAdmin.fieldIntake.review.status.${item.status}` as Parameters<typeof t>[0])}
            </span>
          ) : null}
        </span>
        {decided ? null : (
          <button
            type="button"
            role="checkbox"
            aria-checked={selected.has(item.id)}
            aria-label={label}
            className={`rh-fi-check${selected.has(item.id) ? ' is-on' : ''}`}
            onClick={() => toggle(item.id)}
            data-testid={`fi-check-${item.id}`}
          >
            {selected.has(item.id) ? <Check size={18} strokeWidth={3} aria-hidden="true" /> : null}
          </button>
        )}
      </div>
    );
  };

  const groupFacts = (group: 'property' | 'sale') => facts.filter((i) => fieldDef(i.factKey ?? '')?.group === group);
  const chosen = proposed.filter((i) => selected.has(i.id)).length;
  /* Krok NAB1 — stav bodu: co uz je v navrzich + co makler odskrtl rucne. */
  const allMarks = mergeMarks(marksFromItems(view.items), marks);
  const checklistPoints = checklistKeys(view.missing, marks);

  /** Zahlavi srovnani „řečeno → do zakázky" (jen desktop, `nabor.css`). */
  const compareHead = (
    <div className="rh-fi-cmp" aria-hidden="true">
      <span>{tr('webAdmin.fieldIntake.review.saidHead')}</span>
      <span>{tr('webAdmin.fieldIntake.review.caseHead')}</span>
    </div>
  );

  return (
    <div className="rh-fi-split" data-testid="fi-review">
      <div className="rh-fi-col rh-fi-col--talk rh-fi-only-tablet" data-testid="fi-transcript">
        <h2 className="rh-fi-big">{tr('webAdmin.fieldIntake.review.conversation')}</h2>
        <p className="rh-fi-muted">
          {view.session.consentGivenAt !== null ? tr('webAdmin.fieldIntake.review.consented') : tr('webAdmin.fieldIntake.review.selfDictation')}
        </p>
        {transcript.map((segment) => (
          <div key={`${String(segment.startS)}-${segment.text}`} className="rh-fi-line">
            <span className="rh-fi-time">{formatClock(segment.startS)}</span>
            <span>{segment.text}</span>
          </div>
        ))}
        {view.photos.length > 0 ? (
          <div className="rh-fi-photos">
            {view.photos.map((p) => (
              <img key={p.id} className="rh-fi-thumb rh-fi-thumb--big" src={photoUrl(view.session.id, p.id)} alt={tr('webAdmin.fieldIntake.recording.photo')} />
            ))}
          </div>
        ) : null}
      </div>

      <div className="rh-fi-col rh-fi-col--facts">
        <h2 className="rh-fi-large" data-testid="fi-review-title">
          {tr('webAdmin.fieldIntake.review.title', { facts: facts.length, tasks: tasks.length })}
        </h2>
        <p className="rh-fi-muted">
          {tr('webAdmin.fieldIntake.review.subtitle', { minutes, photos: view.photos.length })}
        </p>

        {view.session.transcriptStatus === 'unavailable' || view.session.transcriptStatus === 'failed' ? (
          <div data-testid="fi-transcript-preparing">
            <PreparingSection copy={TRANSCRIPT_PREPARING} compact />
          </div>
        ) : null}
        {view.session.transcriptStatus === 'done' && !view.extractionAvailable ? (
          <p className="rh-fi-note" data-testid="fi-no-ai">{tr('webAdmin.fieldIntake.review.noAi')}</p>
        ) : null}

        {groupFacts('property').length > 0 ? (
          <>
            <h3 className="rh-fi-h">{tr('webAdmin.fieldIntake.review.groupProperty')}</h3>
            <section className="rh-v4-group" data-testid="fi-group-property">
              {compareHead}
              {groupFacts('property').map(renderItem)}
            </section>
          </>
        ) : null}
        {groupFacts('sale').length > 0 ? (
          <>
            <h3 className="rh-fi-h">{tr('webAdmin.fieldIntake.review.groupSale')}</h3>
            <section className="rh-v4-group" data-testid="fi-group-sale">
              {compareHead}
              {groupFacts('sale').map(renderItem)}
            </section>
          </>
        ) : null}
        {tasks.length > 0 ? (
          <>
            <h3 className="rh-fi-h">{tr('webAdmin.fieldIntake.review.groupTasks')}</h3>
            <section className="rh-v4-group" data-testid="fi-group-tasks">
              {compareHead}
              {tasks.map(renderItem)}
            </section>
          </>
        ) : null}

        {/*
          Krok NAB1: tentyz checklist jako pri nahravani — do NAB1 tu stala
          mrtva kolecka, ktera vypadala jako zaskrtavatka. Odskrtnuti s hodnotou
          zalozi navrh „ručně" a propise se do zakazky.
        */}
        {checklistPoints.length > 0 ? (
          <>
            <h3 className="rh-fi-h">{tr('webAdmin.fieldIntake.recording.stillUnknown')}</h3>
            <div data-testid="fi-missing">
              <ChecklistPanel keys={checklistPoints} marks={allMarks} onMark={onMark} onUnmark={onUnmark} defaultOpen withHint={false} />
            </div>
          </>
        ) : null}

        <details className="rh-v4-group rh-fi-manual" data-testid="fi-manual">
          <summary className="rh-v4-row">{tr('webAdmin.fieldIntake.review.manualTitle')}</summary>
          <label className="rh-v4-form-row">
            <span className="rh-v4-form-row__label">{tr('webAdmin.fieldIntake.review.manualField')}</span>
            <select className="rh-input rh-fi-input" value={manualKey} onChange={(e) => setManualKey(e.target.value)} data-testid="fi-manual-field">
              {INTAKE_FIELD_DEFS.map((f) => (
                <option key={f.factKey} value={f.factKey}>
                  {fieldLabel(f.factKey)}
                </option>
              ))}
            </select>
          </label>
          <label className="rh-v4-form-row">
            <span className="rh-v4-form-row__label">{tr('webAdmin.fieldIntake.review.manualValue')}</span>
            {fieldDef(manualKey)?.valueType === 'boolean' ? (
              <select className="rh-input rh-fi-input" value={manualValue} onChange={(e) => setManualValue(e.target.value)} data-testid="fi-manual-value">
                <option value="">—</option>
                <option value="true">{tr('webAdmin.fieldIntake.value.yes')}</option>
                <option value="false">{tr('webAdmin.fieldIntake.value.no')}</option>
              </select>
            ) : (
              <input className="rh-input rh-fi-input" value={manualValue} onChange={(e) => setManualValue(e.target.value)} data-testid="fi-manual-value" />
            )}
          </label>
          {transcript.length > 0 ? (
            <label className="rh-v4-form-row">
              <span className="rh-v4-form-row__label">{tr('webAdmin.fieldIntake.review.manualQuote')}</span>
              <select className="rh-input rh-fi-input" value={manualQuote} onChange={(e) => setManualQuote(e.target.value)} data-testid="fi-manual-quote">
                <option value="">—</option>
                {transcript.map((s) => (
                  <option key={`${String(s.startS)}-${s.text}`} value={s.text}>
                    {formatClock(s.startS)} {s.text}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div className="rh-v4-row rh-v4-row--static">
            <button type="button" className="rh-v4-btn rh-v4-btn--tinted" onClick={() => void addManual()} data-testid="fi-manual-add">
              {tr('webAdmin.fieldIntake.review.manualAdd')}
            </button>
          </div>
        </details>

        {view.session.caseId === null && cases.length > 0 ? (
          <label className="rh-v4-group rh-v4-form-row">
            <span className="rh-v4-form-row__label">{tr('webAdmin.fieldIntake.review.pickCase')}</span>
            <select className="rh-input rh-fi-input" value={caseId} onChange={(e) => setCaseId(e.target.value)} data-testid="fi-case">
              <option value="">—</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {error !== null ? (
          <p className="rh-fi-note rh-fi-note--warn" role="alert" data-testid="fi-review-error">
            {error}
          </p>
        ) : null}
        {/*
          Krok PIL1 (vada V9): po ulozeni stalo shrnuti a tlacitka V JEDNOM
          odstavci, takze se cetlo „…Nepřepsáno kvůli úřednímu údaji: 0.Otevřít
          zakázku" (snimek `GEN1/4-05`). Veta a tlacitka maji vlastni radky.
        */}
        {result !== null ? (
          <div className="rh-fi-note" role="status" data-testid="fi-saved">
            <p style={{ margin: 0 }}>
              {tr('webAdmin.fieldIntake.review.savedSummary', {
                saved: result.outcomes.filter((o) => o.status === 'saved').length,
                conflicts: result.outcomes.filter((o) => o.status === 'conflict').length,
              })}
            </p>
            {/*
              Krok P8F3 bod 3: selhani napojeni fotek uz nesmi zmizet pod
              vetou „uloženo" (nalez P8-N4: 8x z 8 `photos_attach_failed`).
              Fotky zustavaji u naboru, takze „Zkusit znovu" je DALSI
              POTVRZENI — to je idempotentni a fotku nezdvoji (unikatni index
              `property_media_file_uq`).
            */}
            {result.photos?.failed === true ? (
              <p className="rh-fi-note rh-fi-note--warn" style={{ margin: '8px 0 0' }} data-testid="fi-photos-failed">
                {tr('webAdmin.fieldIntake.review.photosFailed')}{' '}
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--plain"
                  disabled={busy}
                  onClick={() => void save()}
                  data-testid="fi-photos-retry"
                >
                  {tr('webAdmin.fieldIntake.review.photosRetry')}
                </button>
              </p>
            ) : null}
            {result.photos != null && !result.photos.failed && result.photos.attached > 0 ? (
              <p style={{ margin: '8px 0 0' }} data-testid="fi-photos-attached">
                {tr('webAdmin.fieldIntake.review.photosAttached', { attached: result.photos.attached })}
              </p>
            ) : null}
            <div className="rh-fi-note__acts">
              {view.session.caseId !== null ? (
                <button type="button" className="rh-v4-btn rh-v4-btn--plain" onClick={() => onOpenCase(view.session.caseId as string)} data-testid="fi-open-case">
                  {tr('webAdmin.fieldIntake.review.openCase')}
                </button>
              ) : null}
              {result.continueToListing && view.session.caseId !== null ? (
                <button type="button" className="rh-v4-btn rh-v4-btn--plain" onClick={() => onOpenListing(view.session.caseId as string)} data-testid="fi-open-listing">
                  {tr('webAdmin.fieldIntake.review.openListing')}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {/*
          Krok PIL1 (vada V9): po ulozeni uz tlacitko „Uložit 2 z 2…" nema co
          ulozit — polozky jsou potvrzene a dalsi krok je „Pokračovat".
          Rozhoduje VYSLEDEK ulozeni (`result`), ne jen `proposed`: seznam se
          obnovuje az dalsim nactenim relace.
        */}
        {proposed.length > 0 && result === null ? (
          <div className="rh-fi-footer">
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--filled rh-v4-btn--block"
              disabled={busy || chosen === 0}
              onClick={() => void save()}
              data-testid="fi-save"
            >
              {view.listingDraftAvailable
                ? tr('webAdmin.fieldIntake.review.saveToListing', { chosen, total: proposed.length })
                : tr('webAdmin.fieldIntake.review.save', { chosen, total: proposed.length })}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
